import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';

// Premium Glass Section with scroll-based frosted glass transition
export const GlassSection = ({ 
  children, 
  className = '',
  darkSection = false,
}) => {
  const sectionRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  // Smooth spring animation for buttery transitions
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  // Content opacity - fully visible when section is in center of viewport
  const contentOpacity = useTransform(
    smoothProgress,
    [0, 0.15, 0.85, 1],
    [0.5, 1, 1, 0.5]
  );

  // Subtle vertical movement for depth perception
  const contentY = useTransform(
    smoothProgress,
    [0, 0.15, 0.85, 1],
    [30, 0, 0, -30]
  );

  // Glass overlay opacity - appears when section is entering/leaving
  const glassOpacity = useTransform(
    smoothProgress,
    [0, 0.12, 0.88, 1],
    [1, 0, 0, 1]
  );

  // Brightness boost for the lightening glass effect
  const brightness = useTransform(
    smoothProgress,
    [0, 0.15, 0.85, 1],
    [1.15, 1, 1, 1.15]
  );

  // Scale for subtle depth
  const scale = useTransform(
    smoothProgress,
    [0, 0.15, 0.85, 1],
    [0.98, 1, 1, 0.98]
  );

  return (
    <div ref={sectionRef} className={`relative ${className}`}>
      {/* Main content with glass transitions */}
      <motion.div
        style={{ 
          opacity: contentOpacity, 
          y: contentY,
          scale,
        }}
        className="relative z-0"
      >
        {children}
      </motion.div>

      {/* Frosted glass overlay - top */}
      <motion.div
        className="absolute top-0 left-0 right-0 h-32 pointer-events-none z-30"
        style={{
          opacity: glassOpacity,
          background: darkSection 
            ? 'linear-gradient(180deg, rgba(10, 61, 45, 0.6) 0%, transparent 100%)'
            : 'linear-gradient(180deg, rgba(255, 255, 255, 0.7) 0%, transparent 100%)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
        }}
      />

      {/* Frosted glass overlay - bottom */}
      <motion.div
        className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none z-30"
        style={{
          opacity: glassOpacity,
          background: darkSection 
            ? 'linear-gradient(0deg, rgba(10, 61, 45, 0.6) 0%, transparent 100%)'
            : 'linear-gradient(0deg, rgba(255, 255, 255, 0.7) 0%, transparent 100%)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
        }}
      />
    </div>
  );
};

// Simplified CSS-based version for better performance
export const GlassSectionCSS = ({ 
  children, 
  className = '',
}) => {
  const sectionRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 80,
    damping: 25,
  });

  const opacity = useTransform(
    smoothProgress,
    [0, 0.12, 0.88, 1],
    [0.5, 1, 1, 0.5]
  );

  const y = useTransform(
    smoothProgress,
    [0, 0.12, 0.88, 1],
    [25, 0, 0, -25]
  );

  const scale = useTransform(
    smoothProgress,
    [0, 0.12, 0.88, 1],
    [0.985, 1, 1, 0.985]
  );

  return (
    <motion.div
      ref={sectionRef}
      className={`relative ${className}`}
      style={{ opacity, y, scale }}
    >
      {children}
    </motion.div>
  );
};

// Full premium glass effect with all features
export const PremiumGlassSection = ({ 
  children, 
  className = '',
  darkSection = false,
}) => {
  const sectionRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 60,
    damping: 20,
  });

  // Main content transforms
  const contentOpacity = useTransform(smoothProgress, [0, 0.1, 0.9, 1], [0.4, 1, 1, 0.4]);
  const contentY = useTransform(smoothProgress, [0, 0.1, 0.9, 1], [50, 0, 0, -50]);
  const contentScale = useTransform(smoothProgress, [0, 0.1, 0.9, 1], [0.97, 1, 1, 0.97]);
  
  // Glass overlay transforms
  const glassOpacity = useTransform(smoothProgress, [0, 0.15, 0.85, 1], [0.9, 0, 0, 0.9]);
  const blurAmount = useTransform(smoothProgress, [0, 0.15, 0.85, 1], [16, 0, 0, 16]);

  // Brightness/lightening effect
  const brightness = useTransform(smoothProgress, [0, 0.15, 0.85, 1], [1.25, 1, 1, 1.25]);

  const glassColorLight = 'rgba(255, 255, 255, 0.65)';
  const glassColorDark = 'rgba(10, 61, 45, 0.55)';
  const glassColor = darkSection ? glassColorDark : glassColorLight;

  return (
    <div ref={sectionRef} className={`relative overflow-hidden ${className}`}>
      {/* Content wrapper with transforms */}
      <motion.div
        style={{ 
          opacity: contentOpacity, 
          y: contentY,
          scale: contentScale,
        }}
        className="relative z-10"
      >
        {children}
      </motion.div>

      {/* Top frosted glass edge */}
      <motion.div
        className="absolute top-0 left-0 right-0 pointer-events-none z-40"
        style={{
          height: '100px',
          opacity: glassOpacity,
          background: `linear-gradient(180deg, ${glassColor} 0%, transparent 100%)`,
          backdropFilter: `blur(${blurAmount.get()}px)`,
          WebkitBackdropFilter: `blur(${blurAmount.get()}px)`,
        }}
      />

      {/* Bottom frosted glass edge */}
      <motion.div
        className="absolute bottom-0 left-0 right-0 pointer-events-none z-40"
        style={{
          height: '100px',
          opacity: glassOpacity,
          background: `linear-gradient(0deg, ${glassColor} 0%, transparent 100%)`,
          backdropFilter: `blur(${blurAmount.get()}px)`,
          WebkitBackdropFilter: `blur(${blurAmount.get()}px)`,
        }}
      />

      {/* Full glass overlay for stronger effect */}
      <motion.div
        className="absolute inset-0 pointer-events-none z-30"
        style={{
          opacity: useTransform(smoothProgress, [0, 0.2, 0.8, 1], [0.4, 0, 0, 0.4]),
          background: darkSection 
            ? 'radial-gradient(ellipse at center, transparent 40%, rgba(10, 61, 45, 0.3) 100%)'
            : 'radial-gradient(ellipse at center, transparent 40%, rgba(255, 255, 255, 0.4) 100%)',
        }}
      />
    </div>
  );
};

export default GlassSection;
